import { useRef, useState, useEffect, useMemo } from 'react';
import classNames from "classnames"
import { useTableMapState } from "./TableMapState";
import CheckboxGroup from "@/migrated-widgets/table-map/components/molecules/CheckboxGroup";
import { locationProducts } from "./constants";
import { CheckboxGroupItem } from "@/migrated-widgets/table-map/components/molecules/CheckboxGroup/Item";
import { CheckboxIcon } from "@/migrated-widgets/table-map/components/icons/CheckboxIcon";
import { PopoverFilterList } from "@/components/molecules/PopoverFilterList";

const COLLAPSE_THRESHOLD = 500;

export const FilterByProduct: React.FC<{}> = () => {
  const { products, setProducts, distinctProducts } = useTableMapState();
  const containerRef = useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const el = containerRef.current?.closest('[id^="widget-filters-portal-"]')?.parentElement?.parentElement;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setCollapsed(entry.contentRect.width < COLLAPSE_THRESHOLD);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const toggleProduct = (product: string) => {
    setProducts((prev) => {
      if (prev.includes(product)) return products.filter(el => el !== product)
      return [...prev, product]
    })
  }

  const isActiveProduct = (product?: string) => {
    if (!product) return false;
    if (!products.length) return true;
    return !products.includes(product);
  }

  const availableProducts = useMemo(() =>
    locationProducts.filter(p => distinctProducts.includes(p.value)),
    [distinctProducts]
  );

  const popoverItems = useMemo(() =>
    availableProducts.map(p => ({ id: p.value, text: p.name, data: p })),
    [availableProducts]
  );

  const selectedPopoverItems = useMemo(() => {
    if (!products.length) return popoverItems;
    return popoverItems.filter(item => !products.includes(item.id));
  }, [products, popoverItems]);

  const handlePopoverSelectionChange = (newSelected: typeof popoverItems) => {
    const newDeselected = availableProducts
      .filter(p => !newSelected.some(s => s.id === p.value))
      .map(p => p.value);
    setProducts(newDeselected);
  };

  if (collapsed) {
    return (
      <div ref={containerRef}>
        <PopoverFilterList
          items={popoverItems}
          selectedItems={selectedPopoverItems}
          onSelectionChange={handlePopoverSelectionChange}
          triggerLabel={`Products: ${selectedPopoverItems.length}/${popoverItems.length}`}
          width="w-56"
          selectAllCTA
          selectAllLabel={selectedPopoverItems.length === popoverItems.length ? "Unselect All" : "Select All"}
        />
      </div>
    );
  }

  return (
    <div ref={containerRef}>
      <CheckboxGroup checkboxes={{ className: "gap-6", width: "w-full" }}>
        {locationProducts.map((productData) => {
          const product = distinctProducts.find(location => location === productData.value);
          return (
            <CheckboxGroupItem
              key={productData.value}
              onClick={() => product ? toggleProduct(product) : null}
              className={classNames("text-sm text-left text-table-map-black", { "text-gray-400": !product })}
              onHoverClass=""
              cursor={product ? "cursor-pointer" : "cursor-not-allowed"}
            >
              <div className="flex items-center gap-1">
                <div className={classNames(
                  "w-4 h-4 rounded-[3px]", "font-roboto",
                  { "bg-gray-100 border border-table-map-lightGray": product },
                  { "bg-gray-50 border border-gray-300": !product },
                  { [productData.color]: isActiveProduct(product) },
                  { "border-0": isActiveProduct(product) }
                )}>
                  {isActiveProduct(product) && <CheckboxIcon />}
                </div>
                <span className="font-roboto"> {productData.name}</span>
              </div>
            </CheckboxGroupItem>
          );
        })}
      </CheckboxGroup>
    </div>
  )
}
